import { configureStore } from '@reduxjs/toolkit';
import { deviceType } from 'react-device-detect';
// import { save, load } from 'redux-localstorage-simple';
import { Provider as ReduxProvider } from 'react-redux';
import { HashRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import {
  CloseButton,
  IconLoading,
  IconNotificationError,
  IconNotificationInfo,
  IconNotificationSuccess,
} from '@/components/Notification';
import ActiveConnectorManager from '@/connectors/ActiveConnectorManager';

// import ActiveConnectorManager from '@/connectors/ActiveConnectorManager';
import { PrivyProvider } from '@privy-io/react-auth';

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClientAtom } from 'jotai-tanstack-query';
import { Provider as JotaiProvider } from 'jotai/react';
import { useHydrateAtoms } from 'jotai/react/utils';
import { WagmiProvider } from 'wagmi';
import { privyAppId, privyConfig } from './connectors/privy';
import { wagmiConfig } from './connectors/wagmi/config';
import { store, storeReducer } from './features/store';
import { queryClient } from './global';

import AntDConfigProvider from './layout/AntDConfigProvider';

const HydrateAtoms = ({ children }: { children: React.ReactNode }) => {
  useHydrateAtoms(new Map([[queryClientAtom, queryClient]]));
  return <>{children}</>;
};

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any

const WrappedToastContainer = () => {
  // const config = useDappChainConfig();
  return (
    <ToastContainer
      position="bottom-right"
      autoClose={5000}
      hideProgressBar={true}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss={false}
      draggable={false}
      pauseOnHover={false}
      theme="light"
      closeButton={CloseButton}
      toastClassName={deviceType}
      icon={({ type }) => {
        const iconElement: JSX.Element = (type === 'success' && <IconNotificationSuccess />) ||
          (type === 'error' && <IconNotificationError />) ||
          (type === 'info' && <IconLoading />) || <IconNotificationInfo />;

        return (
          <div style={{ position: 'relative' }}>
            {/* <img
              style={{ position: 'absolute', right: '-4px', bottom: '-4px', zIndex: 10, width: 24, height: 24 }}
              src={config?.network.icon}
            /> */}
            {iconElement}
          </div>
        );
      }}
    />
  );
};
export function StoreProviderWrapper({
  children,
  reduxStore = store,
}: {
  children: JSX.Element;
  reduxStore?: typeof store;
}): JSX.Element {
  reduxStore = { ...store, ...reduxStore };
  return (
    <ReduxProvider store={reduxStore}>
      <AntDConfigProvider>
        <PrivyProvider appId={privyAppId} config={privyConfig}>
          <WagmiProvider config={wagmiConfig}>
            <PersistQueryClientProvider
              client={queryClient}
              persistOptions={{
                persister: localStoragePersister,
                maxAge: Infinity,
                dehydrateOptions: {
                  shouldDehydrateQuery: (query) => {
                    return ((query.meta?.isPersist || false) as boolean) && query.state.status === 'success';
                  },
                },
              }}>
              <JotaiProvider>
                <HydrateAtoms>
                  {/* 4. Wrap app in PersistQueryClientProvider */}
                  {/* Your App */}
                  <WrappedToastContainer />

                  <ActiveConnectorManager>
                    <HashRouter>{children}</HashRouter>
                  </ActiveConnectorManager>
                  {/* </QueryClientProvider> */}
                  <ReactQueryDevtools initialIsOpen={false} position="bottom" buttonPosition="bottom-left" />
                </HydrateAtoms>
              </JotaiProvider>
            </PersistQueryClientProvider>
          </WagmiProvider>
        </PrivyProvider>
      </AntDConfigProvider>
    </ReduxProvider>
  );
}

export default function ProviderWrapper({
  children,
  reducer = storeReducer,
}: {
  children: JSX.Element;
  reducer?: Partial<typeof storeReducer>;
}): JSX.Element {
  const sReducer = { ...storeReducer, ...reducer };
  const store = configureStore({
    reducer: sReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
    //     .concat(save({ states: PERSISTED_KEYS })),
    // preloadedState: load({ states: PERSISTED_KEYS, disableWarnings: true }),
  });
  return StoreProviderWrapper({ children, reduxStore: store });
}

export function getProviderWrapper(reducer?: Partial<typeof storeReducer>) {
  // eslint-disable-next-line react/display-name, @typescript-eslint/explicit-module-boundary-types
  return ({ children }: { children: JSX.Element }) => <ProviderWrapper reducer={reducer}>{children}</ProviderWrapper>;
}
